import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Avatar, Button, Container, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavSections } from '../navigation/NavSections';
import { isValidEmail, isValidPassword, isValidPhoneNumber, passwordReminder } from './common';
const debug = process.env.REACT_APP_DEBUG && false;
var SignUpStages;
(function (SignUpStages) {
    SignUpStages[SignUpStages["ASK_USER_TO_SIGN_UP"] = 0] = "ASK_USER_TO_SIGN_UP";
    SignUpStages[SignUpStages["ASK_USER_TO_ENTER_RECEIVED_CODE"] = 1] = "ASK_USER_TO_ENTER_RECEIVED_CODE";
})(SignUpStages || (SignUpStages = {}));
// REQUIRES CDK equivalent of Enable self-registration = true in CognitoUser Pool
// REQUIRES CDH equivalent of Allow Cognito to automatically send messages to verify and confirm
/**
 * Collect User info
 */
const submitSignUpDetails = async (username, password, email, phoneNumber, setMessage, setError, setEmail, setSignUpStage) => {
    const params = {
        username,
        password,
        attributes: {
            email, // optional
            /** Cognito expects `phone_number`, but IoTV / LSC uses `phoneNumber` */
            phone_number: phoneNumber.replace(/ /g, '').replace(/^00/, '+'), // optional - E.164 number convention
            // other custom attributes
        },
    };
    debug && console.log('sign up params', params);
    try {
        const res = await Auth.signUp(params);
        const { user } = res;
        debug && console.log(user);
        if (res) {
            setMessage(`An access code has been sent to your email address ${email}`);
            setSignUpStage(SignUpStages.ASK_USER_TO_ENTER_RECEIVED_CODE);
            //setEmail(successMsg.CodeDeliveryDetails?.Destination)
            //setResetStage(SingUpStages.ASK_USER_TO_ENTER_RECEIVED_CODE)
        }
        else {
            setMessage(JSON.stringify(user));
        }
    }
    catch (e) {
        console.log('Err in requestConfirmationCode', e.message);
        const errMsgMap = {
            default: `Sorry, We dont have an error like that. ${e.message}`,
            'SignUp is not permitted for this user pool': 'Sorry, this system does not allow self sign up',
            'Invalid email address format.': 'This system requires an email address as username',
            'User already exists': `User ${username} already exists`,
            'Invalid phone number format.': `Phone numbers need to start with [00 or +] [country code]`,
        };
        const [_, matchedError] = Object.entries(errMsgMap).find(([errMsg, ourMsg]) => (e.message || '').includes(errMsg)) ?? [
            'none',
            errMsgMap.default,
        ];
        setError(matchedError);
    }
};
// maybe MFA only
const confirmSignUp = async (username, code, setMessage, setError, navigate) => {
    try {
        const res = await Auth.confirmSignUp(username, code);
        debug && console.log('confirmSignUp', res);
        if (res) {
            const successMsg = res;
            if (successMsg) {
                setMessage(`An account has been set up for ${username}, you will be redirected to login`);
                setTimeout(() => navigate(`/${NavSections.LOGIN}`), 1500);
            }
            else {
                setMessage(JSON.stringify(res));
            }
        }
    }
    catch (e) {
        console.log('error confirming sign up', e.message);
        const errMsgMap = {
            default: `Sorry, We dont have an error like that. ${e.message}`,
        };
        const [_, matchedError] = Object.entries(errMsgMap).find(([errMsg, ourMsg]) => (e.message || '').includes(errMsg)) ?? [
            'none',
            errMsgMap.default,
        ];
        setError(matchedError);
    }
};
/**
 * maybe MFA obly
 */
const resendConfirmationCode = async (username) => {
    try {
        await Auth.resendSignUp(username);
        debug && console.log('code resent successfully');
    }
    catch (err) {
        console.log('error resending code: ', err);
    }
};
/**
 * New users must sign up with username being email
 */
const SignUp = ({}) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState(undefined);
    const [password, setPassword] = useState(undefined);
    const [showPassword, setShowPassword] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(undefined);
    const [code, setCode] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [message, setMessage] = useState(undefined);
    const [signUpStage, setSignUpStage] = useState(SignUpStages.ASK_USER_TO_SIGN_UP);
    const emailPasswordPhoneForm = (_jsxs("form", { noValidate: true, children: [_jsx(TextField, { id: 'email',
                name: 'email',
                value: email,
                label: 'Email',
                variant: 'outlined',
                margin: 'dense',
                required: true,
                fullWidth: true,
                autoComplete: 'email',
                autoFocus: true,
                helperText: email === undefined || email.length === 0
                    ? 'Please enter your email address'
                    : !isValidEmail(email)
                        ? `${email} is not a valid email address`
                        : undefined,
                onChange: (e) => setEmail(e.target.value) }, 'email'), _jsx(TextField, { id: 'password',
                name: 'password',
                label: 'Password',
                value: password,
                variant: 'outlined',
                margin: 'dense',
                required: true,
                fullWidth: true,
                type: showPassword ? 'text' : 'password',
                error: !!password && !isValidPassword(password),
                helperText: password === undefined || password.length === 0
                    ? 'Please enter a new password'
                    : password && !isValidPassword(password)
                        ? passwordReminder
                        : undefined,
                onChange: (e) => setPassword(e.target.value),
                InputProps: {
                    endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { "aria-label": "toggle password visibility", onClick: () => setShowPassword(!showPassword), children: showPassword ? _jsx(Visibility, {}) : _jsx(VisibilityOff, {}) }) })),
                } }, 'password'), _jsx(TextField, { id: 'phoneNumber',
                name: 'phoneNumber',
                value: phoneNumber,
                variant: 'outlined',
                label: 'Phone',
                margin: 'dense',
                required: true,
                fullWidth: true,
                autoComplete: 'tel',
                error: phoneNumber !== undefined && !isValidPhoneNumber(phoneNumber),
                helperText: phoneNumber === undefined || phoneNumber.length === 0
                    ? 'Please enter your phone number'
                    : !isValidPhoneNumber(phoneNumber)
                        ? 'Please ensure you have entered the country code correctly, e.g. +64'
                        : undefined,
                onChange: (e) => setPhoneNumber(e.target.value) }, 'phoneNumber'), error && (_jsx(Alert, { severity: "error", onClose: () => setError(undefined), sx: { marginBlockEnd: 1 }, children: error })), message && (_jsx(Alert, { severity: "info", onClose: () => setMessage(undefined), sx: { marginBlockEnd: 1 }, children: message })), _jsx(Button, { type: 'submit',
                disabled: !email || !isValidEmail(email) || !phoneNumber || !isValidPhoneNumber(phoneNumber) || error !== undefined,
                fullWidth: true,
                variant: 'contained',
                color: 'primary',
                onClick: (e) => {
                    const username = email;
                    e.preventDefault();
                    if (username && password && email && phoneNumber) {
                        submitSignUpDetails(username, password, email, phoneNumber, setMessage, setError, setEmail, setSignUpStage);
                        setError(undefined);
                        setMessage(undefined);
                    }
                    else {
                        console.log(`Something missing`, { username, password, email, phoneNumber });
                    }
                }, children: "Sign me up" })] }));
    const enterCodeForm = (_jsxs("form", { noValidate: true, children: [_jsx(TextField, { variant: 'outlined',
                margin: 'normal',
                required: true,
                fullWidth: true,
                id: 'code',
                defaultValue: '',
                label: 'Validation Code',
                name: 'code',
                helperText: `Please enter the code sent to email, ${email}`,
                autoFocus: true,
                onChange: (e) => setCode(e.target.value) }, 'code'), error && (_jsx(Alert, { severity: "error", onClose: () => setError(undefined), sx: { marginBlockEnd: 1 }, children: error })), message && (_jsx(Alert, { severity: "info", onClose: () => setMessage(undefined), sx: { marginBlockEnd: 1 }, children: message })), _jsx(Button, { type: 'submit',
                disabled: code === undefined || code.length === 0,
                fullWidth: true,
                variant: 'contained',
                color: 'primary',
                onClick: (e) => {
                    e.preventDefault();
                    const username = email;
                    if (username && code) {
                        confirmSignUp(username, code, setMessage, setError, navigate);
                        setError(undefined);
                        setMessage(undefined);
                    }
                }, children: "Submit" })] }));
    let form = _jsx(_Fragment, {});
    switch (signUpStage) {
        case SignUpStages.ASK_USER_TO_SIGN_UP:
            form = emailPasswordPhoneForm;
            break;
        case SignUpStages.ASK_USER_TO_ENTER_RECEIVED_CODE:
            form = enterCodeForm;
            break;
    }
    return (_jsx(Container, { component: "main", maxWidth: "xs", sx: { paddingBlock: 1 }, children: _jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "center", alignItems: "center", gap: 1, children: [_jsx(Avatar, { children: _jsx(LockOutlinedIcon, {}) }), _jsx(Typography, { component: "h1", variant: "h5", children: "Sign up" })] }), form, _jsx(Link, { to: `/${NavSections.LOGIN}`, children: "Back to login" })] }) }));
};
export default SignUp;
